import { Dispatch, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import spacetime from 'spacetime';
import { useUnmount } from 'react-use';
import { useHistory } from 'react-router';

import {
  getUserRoleIsOmbudsman,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { getPublicPortalURL } from 'CorePackage/MainModule/reducers/coreReducer';
import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import {
  getIsPlanErknmStatusConsidered,
  getIsPlanErknmStatusUnderConsideration,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ErknmActs } from 'KnmErknmPackage/ErknmActsModule/components/ErknmActs';
import { ErknmApproveInfoDetails } from 'KnmErknmPackage/ErknmApproveInfoDetailsModule/components/ErknmApproveInfoDetails';
import { ErknmEventSubject } from 'KnmErknmPackage/ErknmEventSubjectModule/components/ErknmEventSubject';
import { ErknmInfo } from 'KnmErknmPackage/ErknmInfoModule/components/ErknmInfo';
import { ErknmOtherInfo } from 'KnmErknmPackage/ErknmOtherInfoModule/components/ErknmOtherInfo';
import { ErknmResultDecisions } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisions';
import { ErknmSubject } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubject';
import { KnmDocuments } from 'KnmPackage/KnmDocumentsModule/components/KnmDocuments';
import { KnmHistories } from 'KnmPackage/KnmHistoriesModule/components/KnmHistories';
import { KnmSignInfo } from 'KnmPackage/KnmSignInfoModule/components/KnmSignInfo';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getIsControlObjectsErvkCorrectionRequired,
  getChangePlanReasonAfterConfirmEvent,
  getKnmIsChangePlanReasonActive,
  getKnmIsEditableInStatusCommentsExist,
  getKnmIsErknmStatusApproving,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsErknmStatusDecisionAppealed,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusNotApprove,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsErknmStatusRejectAppealed,
  getKnmIsErknmStatusRejected,
  getKnmIsErknmStatusTypeCantBeCarryOut,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeDisapproved,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsInitialData,
  getKnmIsNew,
  getKnmIsWatchOnly,
  getKnmWatchOnlyByTestUser,
  getKnmIsNeedActSaveOrSign,
  getKnmIsChangeSubjectTypeFromActionsAvailable,
  getIsRsnCngPPXXII,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { ErknmAppealStatus } from 'KnmErknmPackage/ErknmAppealStatusModule/components/ErknmAppealStatus';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { tzMoscow } from 'CorePackage/MainModule/constants/timeZone';
import { KnmPrevious } from 'KnmPackage/KnmPreviousModule/components/KnmPrevious';
import { ErknmStatusExchange } from 'KnmErknmPackage/ErknmStatusExchangeModule/components/ErknmStatusExchange';
import { ErknmPlanHistory } from 'KnmErknmPackage/ErknmPlanHistoryModule/components/ErknmPlanHistory';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getDictionariesTargetEntity } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionariesItem } from 'CorePackage/DictionariesModule/interfaces/IDictionariesItem';
import { TargetEntityTitlesEnum } from 'APIPackage/DictionariesModule/enums/TargetEntityTitlesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ErknmPlanHistoryChanges } from 'KnmErknmPackage/ErknmPlanHistoryModule/components/ErknmPlanHistoryChanges';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { ErknmAgreements } from 'KnmErknmPackage/ErkmAgreementsModule/ErknmAgreements';
import { ErknmApproveChangeKnm } from 'KnmErknmPackage/ErknmApproveInfoDetailsModule/components/ErknmChangePlanApprove';
import { ErknmRepeatPassports } from 'KnmErknmPackage/ErknmRepeatPassportsModule/ErknmRepeatPassports';

import { createOrganizationTemplateSelectOption } from '../common/createOrganizationTemplateSelectOption';
import { createOrganizationTemplateSelectOptionFromDocument } from '../common/createOrganizationTemplateSelectOptionFromDocument';
import { newOrganizationTemplateSelectOption } from '../constants/newOrganizationTemplateSelectOption';
import { useKnmErknm } from '../hooks/useKnmErknm';
import { IKnmErknmForm } from '../interfaces/IKnmErknmForm';
import { IKnmErknmOrganizationTemplateSelectOption } from '../interfaces/IKnmErknmOrganizationTemplateSelectOption';
import { knmErknmResetInitialFormValues, knmErknmSetInitialFormValues } from '../actions/knmErknmActions';
import { getKnmErknmInitialFormValues } from '../selectors/knmErknmSelectors';
import { useKnmInitFillInProcessFields } from '../hooks/useKnmInitFillInProcessFields';
import { catalogGet, catalogKnmTypes248Get } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { CreateErknmModal } from './CreateErknmModal';
import { ROUTE_KNM_LIST } from '../../../CorePackage/MainModule/routes/routes';
import {
  planListGetApprovedForCurrentYear,
  planListSetApprovedForCurrentYear,
} from '../../../PlanListPackage/MainModule/actions/planListActions';
import { KnmNotifications } from '../../../KnmPackage/KnmNotificationModule/components/KnmNotifications';
import { ErknmJudgment } from '../../ErknmJudgmentModule/ErknmJudgment';
import { KnmEpguNotification } from '../../../KnmPackage/KnmEpguNotificationModule/components/KnmEpguNotification';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { ErknmObjects } from '../../ErknmObjectModule/components/ErknmObjects';
import { IInteractionCategorySelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IInteractionCategorySelectOption';
import { getCatalogsInteractionCategorySelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { ErknmEvents } from '../../ErknmEventsModule/ErknmEvents';
import { IKnmErknmFormChangePlanReason } from '../interfaces/IKnmErknmFormChangePlanReason';
import { useHasFieldApproveRemark } from '../common/useHasFieldApproveRemark';
import { ErknmApproveRemarkSectionCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { IKnmFormRiskIndicators } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';

export interface IKnmErknmContent {
  formRef: IKnmFormRef;
  knmData: IKnm | undefined;
  values: IKnmErknmForm;
}

interface IKnmErknmContentStoreProps {
  allEventInteractionCategoryOptions: IInteractionCategorySelectOption[] | undefined;
  initialFormValues: IKnmErknmForm;
  isChangePlanReasonActive: boolean;
  isEditableInStatusCommentsExist: boolean;
  isInitialData: boolean | undefined;
  isKnmStatusApproving: boolean;
  isKnmStatusCommentsExist: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusNotApprove: boolean;
  isKnmStatusReadyForApprove: boolean;
  isKnmStatusRejectAppealed: boolean;
  isKnmStatusRejected: boolean;
  isKnmStatusTypeCantBeCarryOut: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  isKnmStatusTypeDecisionAppealed: boolean;
  isKnmStatusTypeDisapproved: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isNewKnm: boolean;
  isOmbudsmen: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  isWatchOnly: boolean;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  publicPortalURL: string;
  settings: IAdminSetting[] | undefined;
  targetEntity: IDictionariesItem<ITargetEntity>;
  isControlObjectsErvkCorrectionRequired: boolean;
  changePlanReasonAfterConfirm?: IKnmErknmFormChangePlanReason[];
  isWatchOnlyByTestUser: boolean;
  isNeedActSaveOrSign?: boolean;
  isChangeSubjectTypeFromActionsAvailable: boolean;
  isRsnCngPPXXII: boolean;
}

const KnmErknmContentWithoutStore = ({
  formRef,
  knmData,
  values,
  allEventInteractionCategoryOptions,
  initialFormValues,
  isChangePlanReasonActive,
  isEditableInStatusCommentsExist,
  isInitialData,
  isKnmStatusApproving,
  isKnmStatusCommentsExist,
  isKnmStatusFillInProcess,
  isKnmStatusNotApprove,
  isKnmStatusReadyForApprove,
  isKnmStatusRejectAppealed,
  isKnmStatusRejected,
  isKnmStatusTypeCantBeCarryOut,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  isKnmStatusTypeDecisionAppealed,
  isKnmStatusTypeDisapproved,
  isKnmStatusTypeWaitingCarryOut,
  isNewKnm,
  isOmbudsmen,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  isWatchOnly,
  knmPlanStatus,
  publicPortalURL,
  settings,
  targetEntity,
  isControlObjectsErvkCorrectionRequired,
  changePlanReasonAfterConfirm,
  isWatchOnlyByTestUser,
  isNeedActSaveOrSign = false,
  isChangeSubjectTypeFromActionsAvailable,
  isRsnCngPPXXII,
}: IKnmErknmContent & IKnmErknmContentStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const history = useHistory();
  const isObjectsRemarksExists = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.OBJECTS);
  const isKindKnmRemarksExists = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.KIND_KNM);
  const isDatesRemarksExists = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.DATES);
  const isReasonsRemarksExists = useHasFieldApproveRemark(ErknmApproveRemarkSectionCodeEnum.REASONS);
  const isKnmPlanStatusConsidered: boolean = getIsPlanErknmStatusConsidered(knmPlanStatus);
  const isKnmPlanStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(knmPlanStatus);
  const isFizAvailable = !!targetEntity?.data?.values.find(item => item.value.title === TargetEntityTitlesEnum.FIZ);
  const isDisableInitialFields = isInitialData || (!isNewKnm ? !!(knmData?.planId || undefined) : false);

  const [eventInteractionCategoryOptions, setEventInteractionCategoryOptions] = useState(
    allEventInteractionCategoryOptions,
  );

  useKnmInitFillInProcessFields({ values, settings });
  useKnmErknm(values);

  useEffect(() => {
    // При создании нового КНМ предзаполнение идёт из CreateErknmModal; reset по initialFormValues
    // (например после knmErknmSetInitialFormValues для links) затирает выбранные организации.
    if (!initialFormValues || !formRef.current || (isNewKnm && !isInitialData)) {
      return;
    }

    requestAnimationFrame(() => {
      try {
        formRef.current.reset(initialFormValues);
      } catch (error) {
        console.error('Reset error in RAF:', error);
      }
    });
  }, [formRef, initialFormValues, isNewKnm]);

  useUnmount(() => {
    dispatch(knmErknmResetInitialFormValues());
  });

  const riskIndicators = useMemo(() => {
    let indicators: IKnmFormRiskIndicators[] = [];

    values.reasonsErknm?.forEach(e => {
      if (Array.isArray(e?.riskIndicators)) {
        indicators = [...indicators, ...e.riskIndicators];
      }
    });

    return indicators;
  }, [values.reasonsErknm]);

  const knmDataLinks = knmData?.links;
  const formLinksRef = useRef(values.links);

  formLinksRef.current = values.links;

  const isEnablePaperlessPassportDocument = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );
  const isEnableAgreements = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_16_STARTS_FROM,
  );
  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  useEffect(() => {
    dispatch(
      knmErknmSetInitialFormValues({
        links: formLinksRef.current?.map(item => ({
          ...item,
          guid: knmDataLinks?.find(dataItem => dataItem.erpId === item.erpId)?.guid || null,
        })),
      }),
    );
  }, [dispatch, knmDataLinks]);

  const knoOrganizationErknm = values?.knoOrganizationErknm;
  const kindControl = values?.kindControl;
  const knoOrganizationId = knoOrganizationErknm?.value.dictVersionId;
  const supervisionId = kindControl?.value.dictVersionId;
  const formKindKnm = values?.kindKnm;
  const formActs = initialFormValues.acts;

  useEffect(() => {
    const knmDataKnoDictId = knmData?.knmErknm?.knoOrganization?.dictId;
    const knmDataKindDictId = knmData?.knmErknm?.kindControl?.dictId;

    if (isNewKnm && !knmDataKnoDictId && !knmDataKindDictId) {
      return;
    }

    if (knmDataKnoDictId !== knoOrganizationErknm?.value.dictId || knmDataKindDictId !== kindControl?.value.dictId) {
      values?.acts?.forEach((item, index) => {
        formRef.current.change(`acts[${index}].titleSigner` as keyof IKnmForm);
        formRef.current.change(`acts[${index}].position` as keyof IKnmForm);
      });
      formRef.current.change('titleSigner');
      values?.resultDecisions?.forEach((item, index) => {
        formRef.current.change(`resultDecisions[${index}].titleSigner` as keyof IKnmForm);
        item?.inspectors?.forEach((inspector, indexInspector) => {
          formRef.current.change(`resultDecisions[${index}].inspectors[${indexInspector}].position` as keyof IKnmForm);
        });
      });
      values?.inspectorsErknm?.forEach((item, index) => {
        formRef.current.change(`inspectorsErknm[${index}].position` as keyof IKnmForm);
      });
      values?.objectsErknm?.forEach((item, index) => {
        formRef.current.change(`objectsErknm[${index}].riskCategory` as keyof IKnmForm);
        formRef.current.change(`objectsErknm[${index}].objectType` as keyof IKnmForm);
        formRef.current.change(`objectsErknm[${index}].objectKind` as keyof IKnmForm);
        formRef.current.change(`objectsErknm[${index}].objectSubKind` as keyof IKnmForm);
      });
      formRef.current.change('kindKnm');
      if (!isDisableInitialFields) {
        formRef.current.change('typeErknm');
      }
      values?.approveDocs?.forEach((item, index) => {
        formRef.current.change(`approveDocs[${index}].approveDoc` as keyof IKnmForm);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knoOrganizationErknm, kindControl]);

  useEffect(() => {
    if (!isFizAvailable && !!values.organizations?.find(item => item.isFiz)) {
      values.organizations?.forEach((item, index) => {
        formRef.current.change(`organizations[${index}].isFiz` as keyof IKnmForm);
        formRef.current.change(`organizations[${index}].organizationName` as keyof IKnmForm);
      });
    }
  }, [isFizAvailable]);

  useEffect(() => {
    if (
      (values.typeErknm?.code === KnmTypeCodesEnum.PP || values.typeErknm?.code === KnmTypeCodesEnum.PP248P294) &&
      values.prosecutorOrganizationErknm &&
      values.knoOrganizationErknm &&
      values.kindControl?.value
    ) {
      dispatch(
        planListGetApprovedForCurrentYear(
          values.prosecutorOrganizationErknm.id || null,
          values.knoOrganizationErknm.idStr ?? null,
          values.prosecutorOrganizationErknm.ervkUuid || null,
          values.kindControl.value.dictVersionId,
        ),
      );
    } else {
      dispatch(planListSetApprovedForCurrentYear([]));
    }
  }, [values.typeErknm, values.prosecutorOrganizationErknm, values.knoOrganizationErknm]);

  useEffect(() => {
    setEventInteractionCategoryOptions(allEventInteractionCategoryOptions);
  }, [allEventInteractionCategoryOptions]);

  const formKindKnmCode = formKindKnm?.code;
  const formRemoteStatus = values.isRemote;
  const isFormHasCollaboratingOrganizationStatus = values.isHasCollaboratingOrganization;
  const isOtherInfoVisible = formKindKnmCode === KnmKindCodesEnum.MZ || formKindKnmCode === KnmKindCodesEnum.VK;

  const approveStatus = knmData?.knmErknm?.approve?.approved;
  const userDate = spacetime.now(tzMoscow);
  const isDisableSubjectInfo =
    isKnmPlanStatusConsidered && approveStatus === ApprovedTypesEnum.APPROVED && !!knmData?.note;
  const currentStartDate = formatDate({ date: values?.startDate || '', dateFormat: SYSTEM_DATE_FORMAT_FNS });
  const isKnmBegins = values?.startDate ? userDate.isAfter(spacetime(currentStartDate || '')) : false;
  const isDisableAllFieldsForProsecutor =
    isProsecutorOrSuperProsecutor &&
    (isKnmStatusFillInProcess ||
      isKnmStatusReadyForApprove ||
      isKnmStatusRejected ||
      isKnmStatusNotApprove ||
      isKnmStatusCommentsExist ||
      isKnmStatusTypeWaitingCarryOut ||
      isKnmStatusTypeCarriedOut ||
      isKnmStatusTypeCompleted);
  const isDisableFieldsForProsecutor =
    isProsecutorOrSuperProsecutor && (isKnmStatusApproving || isKnmStatusRejectAppealed);
  const isErknmWatchOnlyExtended = isWatchOnly || isDisableAllFieldsForProsecutor;

  const isDisabledForProsecutorOrOmbudsmenByKnmImpossibleReason =
    (isProsecutorOrSuperProsecutor || isOmbudsmen) &&
    (isKnmStatusTypeWaitingCarryOut || isKnmStatusCommentsExist || isKnmStatusRejected || isKnmStatusRejectAppealed);

  const isDisableFieldByPreApproveStatuses = isSupervisor && (isKnmStatusFillInProcess || isKnmStatusReadyForApprove);
  const isDisableFieldByRejectStatuses = isSupervisor && (isKnmStatusRejected || isKnmStatusNotApprove);
  const isDisableFieldByInProcessStatuses =
    isSupervisor &&
    (isKnmStatusApproving || isKnmStatusCommentsExist || isKnmStatusRejectAppealed || isKnmStatusTypeWaitingCarryOut);
  const isDisableFieldByCompleteStatuses = isSupervisor && (isKnmStatusTypeCarriedOut || isKnmStatusTypeCompleted);
  const isDisableErknmResultDecisions = !isKnmStatusTypeCarriedOut && !isKnmStatusTypeCompleted;

  const isDisableFieldsInPlanUnderConsideration =
    (isKnmStatusApproving || isKnmStatusCommentsExist || isKnmStatusTypeWaitingCarryOut || isKnmStatusRejected) &&
    isKnmPlanStatusUnderConsideration;

  const isKnmKindRO = values.kindKnm?.code === KnmKindCodesEnum.RO;
  const isNeedStartCheckAfterConfirmEvent =
    isKnmStatusTypeWaitingCarryOut &&
    changePlanReasonAfterConfirm?.length !== 0 &&
    isSupervisor &&
    values.typeErknm?.code === KnmTypeCodesEnum.PP;

  const [isOnlyProductionObject, setIsOnlyProductionObject] = useState<boolean>(false);
  const [isSomeProductionObject, setIsSomeProductionObject] = useState<boolean>(false);

  useEffect(() => {
    if (values.objectsErknm) {
      const hasOnlyProductionObject = !values.objectsErknm?.some(object => object.objectType?.code !== '3');

      if (hasOnlyProductionObject && !isOnlyProductionObject) {
        setIsOnlyProductionObject(true);
      }
      const hasSomeProductionObject = values.objectsErknm?.some(object => object.objectType?.code === '3');

      if (hasSomeProductionObject === true && !isSomeProductionObject) {
        setIsSomeProductionObject(true);
      }
    }
  }, [values]);

  const areCheckForRsnChngPpXii =
    isNeedStartCheckAfterConfirmEvent &&
    changePlanReasonAfterConfirm?.some(item => item?.reasonChangeType?.code === 'RSN_CHNG_PP_XII') &&
    ((!isKnmKindRO && isOnlyProductionObject) || (isKnmKindRO && isSomeProductionObject));

  const isObjectsEnabledWithRemarks =
    isSupervisor &&
    isKnmStatusCommentsExist &&
    (isObjectsRemarksExists || isKindKnmRemarksExists || isDatesRemarksExists || isReasonsRemarksExists);

  const hasStatusBasedRestrictions =
    (isDisableFieldByCompleteStatuses || isDisableFieldByInProcessStatuses) &&
    !isChangePlanReasonActive &&
    !isChangeSubjectTypeFromActionsAvailable;

  const hasAnyRestriction =
    isErknmWatchOnlyExtended ||
    isDisableFieldsForProsecutor ||
    isDisableFieldByRejectStatuses ||
    isDisableSubjectInfo ||
    hasStatusBasedRestrictions ||
    isDisableFieldsInPlanUnderConsideration;

  const hasWatchOnlyConditions = !isEditableInStatusCommentsExist && !isObjectsEnabledWithRemarks && hasAnyRestriction;

  const isObjectsWatchOnly = hasWatchOnlyConditions || isNeedActSaveOrSign;

  const isDisabledAddingActByKnmImpossibleReason = !(
    isKnmStatusTypeWaitingCarryOut ||
    isKnmStatusCommentsExist ||
    isKnmStatusRejected ||
    isKnmStatusRejectAppealed
  );

  const isDisabledActsAdding =
    isErknmWatchOnlyExtended ||
    (!isKnmBegins && isDisabledAddingActByKnmImpossibleReason) ||
    isDisableFieldByPreApproveStatuses ||
    (isSupervisor && (isKnmStatusApproving || isKnmStatusNotApprove)) ||
    isDisableFieldsForProsecutor ||
    isNewKnm ||
    isDisableFieldsInPlanUnderConsideration ||
    isDisabledForProsecutorOrOmbudsmenByKnmImpossibleReason;

  const isDisableFieldsInPlanUnderConsiderationForProsecutor =
    isProsecutorOrSuperProsecutor && isDisableFieldsInPlanUnderConsideration;

  const basicOrganizationsTemplatesSelectOptions:
    | IKnmErknmOrganizationTemplateSelectOption[]
    | undefined = formRef?.current
    .getState()
    .values.organizations?.filter(
      organization => organization.inn || organization.ogrn || organization.organizationName,
    )
    .map(organization => createOrganizationTemplateSelectOption(organization));

  const addOrganizationTemplateSelectOption = (
    templateSelectOption: IKnmErknmOrganizationTemplateSelectOption | undefined,
    resultTemplateSelectOptionsList: IKnmErknmOrganizationTemplateSelectOption[],
  ) => {
    if (templateSelectOption) {
      if (templateSelectOption.inn && basicOrganizationsTemplatesSelectOptions) {
        if (basicOrganizationsTemplatesSelectOptions.filter(x => x.inn === templateSelectOption.inn).length === 0) {
          resultTemplateSelectOptionsList.push(templateSelectOption);
        }
      } else if (
        !basicOrganizationsTemplatesSelectOptions ||
        basicOrganizationsTemplatesSelectOptions.filter(x => x.id === templateSelectOption.id).length === 0
      ) {
        resultTemplateSelectOptionsList.push(templateSelectOption);
      }
    }
  };

  const actsOrganizationsTemplatesSelectOptions:
    | IKnmErknmOrganizationTemplateSelectOption[]
    | undefined = useMemo(() => {
    if (isNewKnm) {
      return;
    }

    const addingOrganizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] = [];

    formActs?.forEach(formAct => {
      const actOrganizationTemplateSelectOption = createOrganizationTemplateSelectOptionFromDocument(
        formAct.organizationTemplate,
        formAct.organization,
      );

      addOrganizationTemplateSelectOption(
        actOrganizationTemplateSelectOption,
        addingOrganizationsTemplatesSelectOptions,
      );
    });

    if (isEnablePaperlessPassportDocument && values.organizations?.length) {
      return [...(basicOrganizationsTemplatesSelectOptions || []), ...addingOrganizationsTemplatesSelectOptions];
    }

    return [
      ...(basicOrganizationsTemplatesSelectOptions || []),
      ...addingOrganizationsTemplatesSelectOptions,
      newOrganizationTemplateSelectOption,
    ];
  }, [isNewKnm, formActs, basicOrganizationsTemplatesSelectOptions]);

  const decisionsOrganizationsTemplatesSelectOptions:
    | IKnmErknmOrganizationTemplateSelectOption[]
    | undefined = useMemo(() => {
    if (isNewKnm) {
      return;
    }

    const addingOrganizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] = [];

    formActs?.forEach(formAct => {
      const actOrganizationTemplateSelectOption = createOrganizationTemplateSelectOptionFromDocument(
        formAct.organizationTemplate,
        formAct.organization,
      );

      if (
        actOrganizationTemplateSelectOption &&
        addingOrganizationsTemplatesSelectOptions.filter(item => item.id === actOrganizationTemplateSelectOption.id)
          .length === 0
      ) {
        addingOrganizationsTemplatesSelectOptions.push(actOrganizationTemplateSelectOption);
      }
    });

    return addingOrganizationsTemplatesSelectOptions;
  }, [isNewKnm, formActs]);

  const isDocumentsDisabled =
    (isWatchOnly && !isSupervisor && !isProsecutorOrSuperProsecutor) ||
    ((isProsecutorOrSuperProsecutor || isSupervisor) &&
      !isKnmStatusApproving &&
      !isKnmStatusTypeWaitingCarryOut &&
      !isKnmStatusTypeCarriedOut &&
      !isKnmStatusTypeDisapproved &&
      !isKnmStatusRejected &&
      !isKnmStatusTypeCompleted &&
      !isKnmStatusNotApprove &&
      !isKnmStatusCommentsExist &&
      !isKnmStatusRejectAppealed &&
      !isKnmStatusFillInProcess &&
      !isKnmStatusReadyForApprove &&
      !isKnmStatusTypeCantBeCarryOut &&
      !isKnmStatusTypeDecisionAppealed);

  const isJudgmentByStatus = !(isKnmStatusTypeCarriedOut || isKnmStatusTypeCompleted);
  const isJudgmentByRole = isProsecutorOrSuperProsecutor || isOmbudsmen;

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.INTERACTION_CATEGORIES));
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_TYPES_CANCELED));
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_RESPONSIBLE_TYPES));
    dispatch(catalogGet(CommonCatalogsNamesEnum.UAV_TYPES));
  }, [dispatch]);

  useEffect(() => {
    if (knoOrganizationId && supervisionId) {
      dispatch(catalogKnmTypes248Get({ knoVersionId: knoOrganizationId, supervisionVersionId: supervisionId }));
    }
  }, [dispatch, knoOrganizationId, supervisionId]);

  const [isApproveNoteFieldEnabledByStatus, setIsApproveNoteFieldEnabledByStatus] = useState<boolean>(
    !isKnmStatusRejected && !isKnmStatusRejectAppealed,
  );

  useEffect(() => {
    setIsApproveNoteFieldEnabledByStatus(!isKnmStatusRejected && !isKnmStatusRejectAppealed);
  }, [isKnmStatusRejected, isKnmStatusRejectAppealed]);

  const [isShownCreateErknmModal, setIsShownCreateErknmModal] = useState(isNewKnm);

  const backUrl: string | null = useMemo(() => {
    const params = new URLSearchParams(window.location.search);

    return params.get('backUrl');
  }, []);
  const closeCreateErknmModal = () => {
    setIsShownCreateErknmModal(false);

    if (backUrl) {
      history.push(backUrl);
    } else {
      history.push(ROUTE_KNM_LIST);
    }
  };

  // Индекс в полном массиве формы нужен для имён полей, иначе блок
  // привязывается к чужой причине (например, к причине исключения)
  const reasons = values.changePlanReason
    ?.map((planReason: IKnmErknmFormChangePlanReason, formIndex: number) => ({ reason: planReason, formIndex }))
    .filter(
      ({ reason: planReason }) =>
        planReason.reasonChangeType?.code != null &&
        ['RSN_CHNG_PP_XII', 'RSN_CHNG_PP_XX', 'RSN_CHNG_PP_XXII'].includes(planReason.reasonChangeType.code),
    );

  return (
    <>
      {isNewKnm && (
        <CreateErknmModal
          visible={isShownCreateErknmModal}
          onClose={closeCreateErknmModal}
          knoOrganizationErknm={values.knoOrganizationErknm}
          kindControl={values.kindControl}
          reasonsErknm={values.reasonsErknm}
          planGuid={values.planGuid}
          startDate={values.startDate}
          stopDate={values.stopDate}
          kindKnmCode={values.kindKnm?.code}
        />
      )}
      {!isNewKnm && (
        <>
          <ErknmInfo
            erpId={knmData?.erpId}
            watchOnlyExtended={
              isErknmWatchOnlyExtended || isDisableFieldsForProsecutor || isDisableFieldByRejectStatuses
            }
            approveRequired={values.approveRequired?.value}
            typeCode={values.typeErknm?.code}
            isReasonsNotEmpty={values.reasonsErknm ? values.reasonsErknm.length > 0 : false}
            isHasCollaboratingOrganization={isFormHasCollaboratingOrganizationStatus}
            planGuid={values.planGuid}
            abrogation={values.abrogation}
            decisionDisabled={values.decisionDisabled}
            disabledDecisionMessage={values.disabledDecisionMessage}
            accessToken={knmData?.knmErknm?.accessToken}
            knoOrganizationErknm={values.knoOrganizationErknm}
            kindControl={values.kindControl}
            publicPortalURL={publicPortalURL}
            isDurationDaysDisabled={values.isDurationDaysDisabled}
            durationDays={values.durationDays}
            durationHours={values.durationHours}
            directDurationDays={values.directDurationDays}
            planIdFromData={knmData?.planId || undefined}
            created={values.created}
            kindKnmCode={formKindKnmCode}
            eventsErknm={values.eventsErknm}
            suspensionEvents={values.suspensionEvents}
            signed={knmData?.signed}
            signatureInfo={knmData?.signatureInfo}
            knmId={knmData?.id}
            kindKnm={values.kindKnm}
          />
          <ErknmApproveInfoDetails
            approved={values.approved?.value}
            approveRequired={values.approveRequired?.value}
            approveAppeal={values.approveAppeal}
            typeCode={values.typeErknm?.code}
            watchOnlyExtended={
              isErknmWatchOnlyExtended || isDisableFieldByInProcessStatuses || isDisableFieldByCompleteStatuses
            }
            isDisableFieldByPreApproveStatuses={isDisableFieldByPreApproveStatuses || isNewKnm}
            isDisableFieldByRejectStatuses={isDisableFieldByRejectStatuses}
            planId={values.planId}
            isApproveNoteFieldEnabledByStatus={isApproveNoteFieldEnabledByStatus}
            setIsApproveNoteFieldEnabledByStatus={setIsApproveNoteFieldEnabledByStatus}
            changePlanReason={values.changePlanReason}
          />
          {reasons && reasons.length > 0 && (
            <ErknmApproveChangeKnm changePlanReason={reasons} isKnmStatusApproving={isKnmStatusApproving} />
          )}
          <ErknmObjects
            isWatchOnly={isWatchOnlyByTestUser || (!isControlObjectsErvkCorrectionRequired && isObjectsWatchOnly)}
            kindKnmCode={formKindKnmCode}
            addOnlyProductionObject={areCheckForRsnChngPpXii}
            invalidFields={values.invalidFields}
          />
          <ErknmSubject
            disableFieldByCompleteStatuses={isDisableFieldByCompleteStatuses}
            disableFieldByInProcessStatuses={
              (!knmData?.knmErknm?.approve?.noteAppeal ||
                knmData?.knmErknm?.approve?.approved !== ApprovedTypesEnum.APPROVED) &&
              isDisableFieldByInProcessStatuses &&
              !isChangePlanReasonActive
            }
            disableSubjectInfo={isDisableSubjectInfo}
            formKindKnmCode={formKindKnmCode}
            interactionCategory={values.interactionCategory}
            isRemote={formRemoteStatus}
            objects={values.objectsErknm}
            setEventInteractionCategoryOptions={setEventInteractionCategoryOptions}
            typeCode={values.typeErknm?.code}
            organizations={values.organizations}
            watchOnlyExtended={
              isErknmWatchOnlyExtended || isDisableFieldsForProsecutor || isDisableFieldByRejectStatuses
            }
            reasonsErknm={values.reasonsErknm}
            shouldValidationForGeneratedDecision={values.shouldValidationForGeneratedDecision}
            rescheduling={values.rescheduling}
          />
          <ErknmEvents
            rescheduling={values.rescheduling}
            interactionCategoryOptions={eventInteractionCategoryOptions}
            reasonFieldValue={values.reasonsErknm?.[0]}
            events={values.eventsErknm}
          />
          <ErknmEventSubject
            kindKnmCode={formKindKnmCode}
            isChecklistsUsed={values.isChecklistsUsed}
            knoOrganization={values.knoOrganizationErknm}
            kindControl={values.kindControl}
            requirementsNpa={values.requirementsNpaErknm}
            created={values.created}
            reasonFieldValue={values.reasonsErknm?.[0]}
            objects={values.objectsErknm}
            riskIndicators={riskIndicators}
          />
          <KnmNotifications
            watchOnly={
              isWatchOnly ||
              isKnmStatusApproving ||
              isDisableAllFieldsForProsecutor ||
              isDisableFieldByRejectStatuses ||
              isDisableFieldsForProsecutor ||
              isDisableFieldByRejectStatuses ||
              isDisableFieldsInPlanUnderConsiderationForProsecutor
            }
            isWatchOnlyByTestUser={isWatchOnlyByTestUser}
            rescheduling={values.rescheduling}
            created={values.created}
            typeCode={values.typeErknm?.code}
            kindKnmCode={formKindKnmCode}
          />
          {isOtherInfoVisible && (
            <ErknmOtherInfo
              watchOnlyExtended={
                isErknmWatchOnlyExtended ||
                isDisableFieldByRejectStatuses ||
                isDisableFieldsForProsecutor ||
                isDisableFieldByRejectStatuses ||
                isDisableFieldsInPlanUnderConsiderationForProsecutor
              }
              hasActs={Array.isArray(values.acts) && values.acts?.length > 0}
              kindKnmCode={formKindKnmCode}
              isSelection={values.isSelection?.value}
              serviceDocumentAttachmentsNumber={values.serviceDocument?.attachments?.length || 0}
              isPresence={values.isPresence?.value}
            />
          )}
          <ErknmAppealStatus
            watchOnlyExtended={
              isErknmWatchOnlyExtended ||
              isDisableFieldByPreApproveStatuses ||
              isDisableFieldByInProcessStatuses ||
              isDisableFieldByCompleteStatuses ||
              isDisableFieldByRejectStatuses ||
              isDisableFieldsForProsecutor ||
              isDisableFieldByRejectStatuses ||
              isNewKnm ||
              isDisableFieldsInPlanUnderConsideration
            }
          />
          <ErknmActs
            acts={values?.acts}
            completedRO={values?.completedRO}
            inspectorsErknm={values?.inspectorsErknm}
            expertsErknm={values?.experts}
            isDisabledAddingActByKnmImpossibleReason={isDisabledAddingActByKnmImpossibleReason}
            kindControl={values.kindControl}
            kindKnmCode={formKindKnmCode}
            knoOrganization={values.knoOrganizationErknm}
            organizations={values.organizations}
            organizationsTemplatesSelectOptions={actsOrganizationsTemplatesSelectOptions}
            requirementsNpa={values.requirementsNpaErknm}
            objects={values.objectsErknm}
            riskIndicators={riskIndicators}
            startDate={values.startDate}
            watchOnlyExtended={isDisabledActsAdding}
            events={values?.eventsErknm}
          />
          <ErknmResultDecisions
            watchOnlyExtended={
              isErknmWatchOnlyExtended ||
              !isKnmBegins ||
              isDisableFieldByPreApproveStatuses ||
              isDisableFieldByInProcessStatuses ||
              isDisableFieldsForProsecutor ||
              isDisableFieldByRejectStatuses ||
              isNewKnm ||
              isDisableErknmResultDecisions ||
              isDisableFieldsInPlanUnderConsideration
            }
            kindKnmCode={formKindKnmCode}
            acts={formActs}
            agreements={values.agreements}
            eventsErknm={values.eventsErknm}
            actsValue={values.acts}
            organizations={values.organizations}
            knoOrganization={values.knoOrganizationErknm}
            organizationsTemplatesSelectOptions={decisionsOrganizationsTemplatesSelectOptions}
            resultDecisions={values.resultDecisions}
            created={values.created}
            isKnmKindRO={isKnmKindRO}
          />
          {isEnableAgreements && (
            <ErknmAgreements resultDecisionsKnm={values.resultDecisions} agreements={values.agreements} isKnm />
          )}
          <ErknmJudgment isWatchOnlyByStatus={isJudgmentByStatus} isWatchOnlyByRole={isJudgmentByRole} />
          <KnmDocuments
            watchOnly={isDocumentsDisabled || isWatchOnlyByTestUser || isNeedActSaveOrSign || isRsnCngPPXXII}
            formInstance={formRef.current}
            title="Документы контрольного (надзорного) мероприятия"
            typeErknm={values.typeErknm}
            kindKnm={values.kindKnm}
            knmDocuments={knmData?.documents}
            disableDeleteDocuments={isProsecutorOrSuperProsecutor && !isKnmStatusApproving}
            decisionDisabled={values.decisionDisabled}
          />
          {knmData?.type?.code !== KnmTypeCodesEnum.PP248P294 && (
            <KnmSignInfo
              title="Сведения о подписании КНМ"
              notificationText="Паспорт КНМ ранее не был подписан"
              signaturesInfo={knmData?.signatureInfo}
              knmDocuments={knmData?.documents}
            />
          )}
          <KnmEpguNotification
            organizations={values.organizations}
            isWatchOnlyByTestUser={isWatchOnlyByTestUser}
            type={'KNM'}
          />
          <KnmPrevious isNewKnm={isNewKnm} organizations={knmData?.knmErknm?.organizations} />
          <ErknmStatusExchange data={knmData?.knmErknm?.webNotificationInfos} created={values.created} />
          {isEnablePrescriptionCheck && <ErknmRepeatPassports repeatPassports={values?.repeatPassports} />}
          <KnmHistories title="История изменений" executionTitle="История проведения КНМ" />
          <ErknmPlanHistory />
          <ErknmPlanHistoryChanges />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmErknmContentStoreProps => ({
  allEventInteractionCategoryOptions: getCatalogsInteractionCategorySelectOptions(state),
  initialFormValues: getKnmErknmInitialFormValues(state),
  isChangePlanReasonActive: getKnmIsChangePlanReasonActive(state) || false,
  isEditableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
  isInitialData: getKnmIsInitialData(state),
  isKnmStatusApproving: getKnmIsErknmStatusApproving(state),
  isKnmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusNotApprove: getKnmIsErknmStatusNotApprove(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isKnmStatusRejectAppealed: getKnmIsErknmStatusRejectAppealed(state),
  isKnmStatusRejected: getKnmIsErknmStatusRejected(state),
  isKnmStatusTypeCantBeCarryOut: getKnmIsErknmStatusTypeCantBeCarryOut(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isKnmStatusTypeDecisionAppealed: getKnmIsErknmStatusDecisionAppealed(state),
  isKnmStatusTypeDisapproved: getKnmIsErknmStatusTypeDisapproved(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isNewKnm: getKnmIsNew(state),
  isOmbudsmen: getUserRoleIsOmbudsman(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  publicPortalURL: getPublicPortalURL(state),
  settings: getAdminFetchSettings(state),
  targetEntity: getDictionariesTargetEntity(state),
  isControlObjectsErvkCorrectionRequired: getIsControlObjectsErvkCorrectionRequired(state),
  changePlanReasonAfterConfirm: getChangePlanReasonAfterConfirmEvent(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  isChangeSubjectTypeFromActionsAvailable: getKnmIsChangeSubjectTypeFromActionsAvailable(state),
  isRsnCngPPXXII: getIsRsnCngPPXXII(state),
});

export const KnmErknmContent = connect(mapStateToProps)(KnmErknmContentWithoutStore);
